import type { components } from '../../api/generated/schema'
import { formatCents, parseMoneyInput } from '../../api/parseMoney'
import type { Bill } from './recurringWire'

type UpdateBody = components['schemas']['RecurringUpdate']

/**
 * What the Bill settings panel edits. `reminderDays` is a plain number: the
 * backend column is a non-null integer, so there is no "no reminder" `null` —
 * **Never is `0`**, and `null` in a PATCH means "not sent" (`RecurringUpdate`
 * declares `reminder_days: int | None = Field(None, ge=0, le=30)` and applies
 * only set keys). Sending `null` for Never would silently change nothing.
 */
export type SettingsDraft = {
  reminderDays: number
  amountType: 'fixed' | 'variable'
  /** Text, as typed: parsed once, at Save, by `parseMoneyInput`. */
  expectedText: string
  tags: string[]
}

/** The backend default for a bill the server sent no reminder for. */
export const DEFAULT_REMINDER_DAYS = 3
/** The backend's ceiling (`le=30`). */
export const MAX_REMINDER_DAYS = 30

/** iOS `reminderOptions`, in its order. */
export const REMINDER_PRESETS = [0, 1, 2, 3, 5, 7, 14] as const

/** iOS `reminderLabel`. */
export function reminderLabel(days: number): string {
  if (days === 0) return 'Never'
  return days === 1 ? '1 day before' : `${days} days before`
}

/**
 * The options the Select offers. A stored value outside the presets (any 1–30
 * from another client) is inserted in order, so the control never shows a value
 * the bill does not have.
 */
export function reminderOptions(current: number): { value: number; label: string }[] {
  const values: number[] = [...REMINDER_PRESETS]
  if (!values.includes(current)) values.push(current)
  return values.sort((a, b) => a - b).map((value) => ({ value, label: reminderLabel(value) }))
}

export const SETTINGS_COPY = {
  reminderNever: "We won't remind you before this bill is due.",
  reminderOn: "We'll remind you before this bill is due.",
  fixedNote: "We'll expect the same amount every time, and flag a payment that doesn't match.",
  variableNote: "The amount changes each time, so we won't flag a payment for being a different size.",
  expectedRequired: 'A fixed bill needs the amount you expect each time.',
  expectedUnreadable: "That isn't an amount we can read.",
  expectedTooSmall: 'Enter an amount greater than zero.',
  /** NEW COPY (shape O3): iOS says "Change Name above"; there is no Name field here. */
  merchantNote: "We match payments against the bill's name. Edit the bill to change what we look for.",
  pauseNote: 'Pausing stops the countdown and the reminders. Nothing is deleted, and you can resume whenever you like.',
  pausedNote: "This bill is paused. It won't appear as due and we won't match payments to it. Resume whenever you like.",
  deleteNote: 'Deleting removes the bill and its schedule. The payments themselves stay on your transactions.',
} as const

export const reminderNote = (days: number): string => (days === 0 ? SETTINGS_COPY.reminderNever : SETTINGS_COPY.reminderOn)

/**
 * Case-insensitive, first spelling wins, blanks dropped: "Netflix" then
 * "netflix" is one tag. The server stores the list as given, so this is the
 * only place the rule can live.
 */
export function dedupeTags(tags: readonly string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of tags) {
    const tag = raw.trim()
    const key = tag.toLowerCase()
    if (tag === '' || seen.has(key)) continue
    seen.add(key)
    out.push(tag)
  }
  return out
}

/** The draft a bill opens with. A variable bill opens with no expected amount: it has none to show. */
export function draftOfBill(bill: Bill): SettingsDraft {
  const fixed = bill.amountType === 'fixed'
  return {
    reminderDays: bill.reminderDays ?? DEFAULT_REMINDER_DAYS,
    amountType: fixed ? 'fixed' : 'variable',
    expectedText: fixed ? formatCents(bill.expectedCents) : '',
    tags: dedupeTags(bill.tags),
  }
}

/** Equal as the panel reads them: the tags compare as a list, in order. */
export function sameDraft(a: SettingsDraft, b: SettingsDraft): boolean {
  return (
    a.reminderDays === b.reminderDays &&
    a.amountType === b.amountType &&
    a.expectedText.trim() === b.expectedText.trim() &&
    a.tags.length === b.tags.length &&
    a.tags.every((t, i) => t === b.tags[i])
  )
}

export type SettingsErrors = { expected?: string; reminder?: string }

/**
 * Cents of the expected amount, or why there are none. Parsing is
 * `parseMoneyInput` and nothing else.
 */
function expectedCentsOf(d: SettingsDraft): { cents: number } | { error: string } {
  if (d.expectedText.trim() === '') return { error: SETTINGS_COPY.expectedRequired }
  let cents: number
  try {
    cents = parseMoneyInput(d.expectedText)
  } catch {
    return { error: SETTINGS_COPY.expectedUnreadable }
  }
  return cents > 0 ? { cents } : { error: SETTINGS_COPY.expectedTooSmall }
}

/** Every rule a draft must pass before anything is sent. */
export function validateSettings(d: SettingsDraft): SettingsErrors {
  const errors: SettingsErrors = {}
  if (!Number.isInteger(d.reminderDays) || d.reminderDays < 0 || d.reminderDays > MAX_REMINDER_DAYS) {
    errors.reminder = `Choose between 0 and ${MAX_REMINDER_DAYS} days.`
  }
  if (d.amountType === 'fixed') {
    const read = expectedCentsOf(d)
    if ('error' in read) errors.expected = read.error
  }
  return errors
}

/**
 * `PATCH /recurring/{id}`'s body: only the keys that differ from `seed` (what
 * the panel opened with, or was last re-seeded to), plus the `version` the
 * user last SAW. Null when nothing changed, so Save with no edits sends nothing.
 *
 * - **Fixed always travels as a pair.** The server rejects `amount_type:
 *   'fixed'` without `expected_amount` (422), so a change to either sends both.
 * - **Variable sends `amount_type` alone**; a leftover expected amount stays on
 *   the server and is ignored for a variable bill.
 * - **Tags replace**: the whole list, de-duplicated.
 *
 * The draft must have passed `validateSettings`.
 */
export function settingsBody(seed: SettingsDraft, d: SettingsDraft, version: number): UpdateBody | null {
  const body: UpdateBody = {}
  if (d.reminderDays !== seed.reminderDays) body.reminder_days = d.reminderDays
  if (d.amountType === 'fixed') {
    const read = expectedCentsOf(d)
    if ('cents' in read && (seed.amountType !== 'fixed' || read.cents !== expectedOf(seed))) {
      body.amount_type = 'fixed'
      body.expected_amount = formatCents(read.cents)
    }
  } else if (seed.amountType !== 'variable') {
    body.amount_type = 'variable'
  }
  const tags = dedupeTags(d.tags)
  if (!sameTags(tags, dedupeTags(seed.tags))) body.tags = tags
  if (Object.keys(body).length === 0) return null
  return { ...body, version }
}

function expectedOf(d: SettingsDraft): number | null {
  const read = expectedCentsOf(d)
  return 'cents' in read ? read.cents : null
}

const sameTags = (a: string[], b: string[]): boolean => a.length === b.length && a.every((t, i) => t === b[i])
